import { apiUrl } from '../config'
import { tokenStorage } from '@/services/tokenStorage'

const UPLOAD_API_PATH_V2 = '/common/upload-image-v2'
const UPLOAD_FILE_API_PATH_V2 = '/common/upload-file-v2'
const UPLOAD_API_PATH_LEGACY = '/common/upload'
const UPLOAD_API_PATH_V3 = '/common/object-storage/upload'

interface UploadResponseData {
  id: number
  cloudflareId: string
  filename: string
  uploaded: string
  requireSignedURLs: boolean
  variants: string[]
  createdAt: string
  updatedAt: string
  url: string
}

interface ApiResponse {
  message: string
  statusCode: number
  data: UploadResponseData
}

const parseUploadResponse = async (response: Response): Promise<string> => {
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    if (response.status === 413) {
      errorMessage = 'File quá lớn. Hệ thống sẽ tự động nén và thử lại.'
    } else {
      try {
        const errorData = await response.json().catch(() => null)
        if (errorData?.message) errorMessage = errorData.message
      } catch {
        // ignore
      }
    }
    throw new Error(errorMessage)
  }
  const responseData: ApiResponse = await response.json()
  return responseData.data.url
}

const postUpload = async (path: string, file: File): Promise<string> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await fetch(`${apiUrl}${path}`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${tokenStorage.getAccessToken()}`,
    },
  })
  return parseUploadResponse(response)
}

export const getImageDisplayUrl = (url: string | null | undefined): string => {
  if (!url || typeof url !== 'string') return ''
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image/')) return url
  return `${apiUrl}${url.startsWith('/') ? url : `/${url}`}`
}

/** `POST /common/upload-image-v2` — endpoint upload ảnh chính trên BE. */
export const uploadImageV2 = async (file: File): Promise<string> => {
  try {
    return await postUpload(UPLOAD_API_PATH_V2, file)
  } catch (error) {
    console.error('Error uploading image (v2):', error)
    throw error
  }
}

/** `POST /common/upload-file-v2` — upload tệp đính kèm chat/tài liệu. */
export const uploadFileV2 = async (file: File): Promise<string> => {
  try {
    return await postUpload(UPLOAD_FILE_API_PATH_V2, file)
  } catch (error) {
    console.error('Error uploading file (v2):', error)
    throw error
  }
}

/** Alias tương thích code cũ — cùng endpoint v2. */
export const uploadImage = uploadImageV2

/**
 * Tải ảnh từ URL ngoài (CDN khách) → BE upload R2 hệ thống mình.
 * `POST /common/upload-image-from-url` → trả `data.url` (URL nội bộ).
 */
export const uploadImageFromUrl = async (sourceUrl: string): Promise<string> => {
  const response = await fetch(`${apiUrl}/common/upload-image-from-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenStorage.getAccessToken()}`,
    },
    body: JSON.stringify({ url: sourceUrl }),
  })
  return parseUploadResponse(response)
}

/** `POST /common/upload` — upload Cloudflare R2 (API cũ). */
export const uploadImageLegacy = async (file: File): Promise<string> => {
  try {
    return await postUpload(UPLOAD_API_PATH_LEGACY, file)
  } catch (error) {
    console.error('Error uploading image (legacy):', error)
    throw error
  }
}

/** `POST /common/object-storage/upload` */
export const uploadImageV3 = async (file: File): Promise<string> => {
  try {
    return await postUpload(UPLOAD_API_PATH_V3, file)
  } catch (error) {
    console.error('Error uploading image (v3):', error)
    throw error
  }
}

interface DeleteResponse {
  message?: string
  statusCode?: number
}

/** BE không hỗ trợ xóa file upload — giữ stub để không gãy caller cũ. */
export const deleteUploadedFile = async (_fileUrl: string): Promise<DeleteResponse> => {
  console.warn('deleteUploadedFile: backend chưa có API xóa ảnh upload')
  return { message: 'not_supported' }
}
